import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { SiTwitch, SiKofi, SiTiktok } from 'react-icons/si';

const navItems = [
  { text: 'Home', path: '/', external: false },
  { text: 'Gallery', path: '/gallery', external: false },
  { text: 'Discount Codes', path: '/DiscountCodes', external: false },
  { text: 'Merch', path: 'https://runerek-shop.fourthwall.com/', external: true },
];

const socialLinks = [
  { 
    name: 'Twitch', 
    url: 'https://twitch.tv/runerek', 
    icon: SiTwitch,
    color: '#9146FF'
  },
  { 
    name: 'Twitter', 
    url: 'https://twitter.com/runerek', 
    icon: TwitterIcon,
    color: '#1DA1F2'
  },
  { 
    name: 'YouTube', 
    url: 'https://www.youtube.com/channel/UChUcBbqoOqkKbap6tARf4qg', 
    icon: YouTubeIcon,
    color: '#FF0000'
  },
  { 
    name: 'Ko-fi', 
    url: 'https://ko-fi.com/runerek', 
    icon: SiKofi,
    color: '#FF5E5B'
  },
  { 
    name: 'TikTok', 
    url: 'https://www.tiktok.com/@runereklive?is_from_webapp=1&sender_device=pc', 
    icon: SiTiktok,
    color: '#000000'
  },
];

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show navbar when at top of page
      if (currentScrollY < 10) {
        setIsVisible(true);
      }
      // Hide when scrolling down, show when scrolling up
      else if (currentScrollY > lastScrollY) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ width: 250 }}>
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.text}
            component={item.external ? "a" : RouterLink}
            to={item.external ? undefined : item.path}
            href={item.external ? item.path : undefined}
            target={item.external ? "_blank" : undefined}
            rel={item.external ? "noopener noreferrer" : undefined}
            onClick={handleDrawerToggle}
            sx={{
              color: 'text.primary',
              '&:hover': { backgroundColor: 'primary.light', color: 'white' },
            }}
          >
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Follow Runerek
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {socialLinks.map((social) => {
            const IconComponent = social.icon;
            return (
              <IconButton
                key={social.name}
                component="a"
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    color: social.color,
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
                aria-label={social.name}
              >
                <IconComponent size={18} />
              </IconButton>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  return (
    <AppBar 
      position="sticky" 
      color="default" 
      elevation={1}
      sx={{
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.3s ease-in-out',
        backgroundImage: 'url(/nav_background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
      }}    >
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src="/logo.gif"
            alt="Runerek Logo"
            sx={{ height: 40, mr: 2 }}
          />
          {!isMobile && (
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'FranxurterTotallyMedium', sans-serif",
                color: 'white',
                display: 'block'
              }}
            >
              Runerek
            </Typography>
          )}
        </Box>
        {isMobile ? (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: 'white' }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }}
            >
              {drawer}
            </Drawer>
          </>
        ) : (
          <>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              {navItems.map((item) => (
                <Button
                  key={item.text}
                  component={item.external ? "a" : RouterLink}
                  to={item.external ? undefined : item.path}
                  href={item.external ? item.path : undefined}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.main', color: 'white' },
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <IconButton
                    key={social.name}
                    component="a"
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: 'white',
                      '&:hover': {
                        color: 'white',
                        backgroundColor: social.color,
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                    aria-label={social.name}
                  >
                    <IconComponent size={18} />
                  </IconButton>
                );
              })}
            </Box>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;